// Stable identifiers are part of the backend contract. Version a new experiment
// instead of changing the allocation or meaning of an existing arm.
export const LANDING_EXPERIMENT_ID = 'landing_create_v5'
export const LANDING_EXPERIMENTS = Object.freeze({
  landing_message_v3: Object.freeze({
    id: 'landing_message_v3',
    cookie: 'pc_landing_v3',
    routePrefix: '/landing-v1',
    variants: Object.freeze(['control', 'chat', 'create']),
    labels: Object.freeze({
      control: 'A · Original',
      chat: 'B · Chat',
      create: 'C · Create',
    }),
    baseline: 'control',
    originalHomepage: 'control',
  }),
  landing_create_v4: Object.freeze({
    id: 'landing_create_v4',
    cookie: 'pc_landing_v4',
    routePrefix: '/landing-v4',
    variants: Object.freeze(['control', 'create']),
    labels: Object.freeze({
      control: 'A · Original',
      create: 'B · Create + Chat section',
    }),
    baseline: 'control',
    originalHomepage: 'control',
  }),
  landing_create_v5: Object.freeze({
    id: 'landing_create_v5',
    cookie: 'pc_landing_v5',
    routePrefix: '/landing-v5',
    variants: Object.freeze(['current', 'revised']),
    labels: Object.freeze({
      current: 'A · Current Create',
      revised: 'B · Revised Create',
    }),
    baseline: 'current',
  }),
})
export const LANDING_COOKIE = LANDING_EXPERIMENTS[LANDING_EXPERIMENT_ID].cookie
export const LANDING_COOKIES = Object.values(LANDING_EXPERIMENTS).map(
  (experiment) => experiment.cookie,
)
export const LANDING_TOKEN_PARAM = 'pc_landing_token'
export const LANDING_TOKEN_MAX_AGE = 90 * 24 * 60 * 60
export const LANDING_VARIANTS =
  LANDING_EXPERIMENTS[LANDING_EXPERIMENT_ID].variants
export const LANDING_VARIANT_LABELS =
  LANDING_EXPERIMENTS[LANDING_EXPERIMENT_ID].labels

export function landingExperiment(id = LANDING_EXPERIMENT_ID) {
  return Object.hasOwn(LANDING_EXPERIMENTS, id) ? LANDING_EXPERIMENTS[id] : null
}

// ISO 3166-1 alpha-2, with ZZ reserved for unavailable geolocation. Browser
// language, account country and preference cookies are not assignment country.
const LANDING_COUNTRIES = new Set(
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW ZZ'.split(
    ' ',
  ),
)

export function normalizeLandingCountry(value) {
  const country = typeof value === 'string' ? value.trim().toUpperCase() : 'ZZ'
  return LANDING_COUNTRIES.has(country) ? country : 'ZZ'
}

export function landingVariantPath(experimentId, variant) {
  const experiment = landingExperiment(experimentId)
  if (!experiment?.variants.includes(variant)) return null
  return variant === experiment.originalHomepage
    ? '/'
    : `${experiment.routePrefix}/${variant}`
}

export function isInternalLandingPath(path) {
  return Object.values(LANDING_EXPERIMENTS).some((experiment) =>
    experiment.variants.some(
      (variant) =>
        path !== '/' && landingVariantPath(experiment.id, variant) === path,
    ),
  )
}

// Email continuations keep their original enrollment. Otherwise an older phase
// takes precedence, so switching the live experiment never re-randomizes it.
export function landingTokenCandidates(readCookie, fromEmail) {
  return [
    ...new Set([fromEmail, ...LANDING_COOKIES.map(readCookie)].filter(Boolean)),
  ]
}

export function chooseLandingVariant(
  fill = (bytes) => crypto.getRandomValues(bytes),
  experimentId = LANDING_EXPERIMENT_ID,
) {
  const variants = landingExperiment(experimentId)?.variants
  if (!variants) throw new Error('Unknown landing experiment')
  const ceiling = Math.floor(256 / variants.length) * variants.length
  const bytes = new Uint8Array(1)
  do {
    fill(bytes)
  } while (bytes[0] >= ceiling)
  return variants[bytes[0] % variants.length]
}

export function landingPageMatches(assignment, rendered) {
  const experiment = landingExperiment(assignment?.experiment_id)
  if (
    !experiment?.variants.includes(assignment?.variant) ||
    !rendered ||
    rendered.landingVariant !== assignment.variant
  )
    return false
  return (
    rendered.landingExperiment === assignment.experiment_id ||
    (assignment.variant === experiment.originalHomepage &&
      rendered.landingExperiment === 'baseline')
  )
}

export function decodeLandingToken(token, now = Date.now()) {
  if (typeof token !== 'string' || token.length > 2048) return null
  const parts = token.split('.')
  if (parts.length !== 2 || !/^[a-f0-9]{64}$/.test(parts[1])) return null
  try {
    const payload = JSON.parse(
      atob(parts[0].replace(/-/g, '+').replace(/_/g, '/')),
    )
    if (
      payload.v !== 1 ||
      !landingExperiment(payload.experiment_id)?.variants.includes(
        payload.variant,
      ) ||
      payload.landing_path !== '/' ||
      !/^[a-f0-9-]{36}$/.test(payload.enrollment_id) ||
      !Number.isInteger(payload.assigned_at) ||
      payload.assigned_at > Math.floor(now / 1000) + 60 ||
      payload.assigned_at < Math.floor(now / 1000) - LANDING_TOKEN_MAX_AGE ||
      (payload.experiment_id === 'landing_create_v5' &&
        !Object.hasOwn(payload, 'country_at_enrollment')) ||
      (Object.hasOwn(payload, 'country_at_enrollment') &&
        (typeof payload.country_at_enrollment !== 'string' ||
          normalizeLandingCountry(payload.country_at_enrollment) !==
            payload.country_at_enrollment))
    )
      return null
    return payload
  } catch {
    return null
  }
}

export function isLandingPrefetch(headers) {
  return (
    headers.has('next-router-prefetch') ||
    headers.has('next-router-segment-prefetch') ||
    /prefetch/i.test(
      `${headers.get('purpose') || ''} ${headers.get('sec-purpose') || ''}`,
    )
  )
}

export function isKnownLandingBot(headers) {
  return /Googlebot|bingbot|DuckDuckBot|Applebot|YandexBot|Baiduspider|GPTBot|OAI-SearchBot|ChatGPT-User|ClaudeBot|PerplexityBot|facebookexternalhit|Facebot|Twitterbot|LinkedInBot|Slackbot|Discordbot|TelegramBot|WhatsApp|HeadlessChrome|Lighthouse/i.test(
    headers.get('user-agent') || '',
  )
}
